/**
 * Last-resort crash recovery for the field: when a render error escapes every error
 * boundary, React 19 removes the root content and the user is left staring at a blank
 * screen. This shows a minimal recovery overlay (Reload / Home) instead, and records the
 * crash to {@link crashDiagnostics}.
 *
 * No React component is used (the tree has just unmounted, and per the app's "functional
 * components only" rule there is no class error boundary) — the overlay is injected
 * straight into the DOM with inline styles, deliberately independent of the app's stylesheet
 * and component tree so it works even when those are the thing that broke.
 */
import { crashDiagnostics } from './crashDiagnosticsService';

const OVERLAY_ID = 'bdk-crash-overlay';

export function showRecoveryOverlay(): void {
  if (typeof document === 'undefined' || document.getElementById(OVERLAY_ID))
    return;

  const overlay = document.createElement('div');
  overlay.id = OVERLAY_ID;
  overlay.setAttribute('role', 'alertdialog');
  overlay.setAttribute('aria-label', 'Application error');
  overlay.style.cssText =
    'position:fixed;inset:0;z-index:2147483647;display:flex;flex-direction:column;' +
    'align-items:center;justify-content:center;gap:20px;padding:24px;text-align:center;' +
    'background:#00305f;color:#fff;font-family:system-ui,-apple-system,sans-serif;';

  const title = document.createElement('div');
  title.textContent = 'Something went wrong';
  title.style.cssText = 'font-size:20px;font-weight:600;';

  const body = document.createElement('div');
  body.textContent =
    'The app hit an unexpected error. Your saved inspections are stored on this device — reloading is safe and your work is not lost.';
  body.style.cssText = 'max-width:360px;line-height:1.5;opacity:0.85;';

  const buttonStyle =
    'min-width:160px;padding:12px 20px;border:none;border-radius:6px;font-size:16px;' +
    'font-weight:600;cursor:pointer;';

  const reload = document.createElement('button');
  reload.textContent = 'Reload';
  reload.style.cssText = buttonStyle + 'background:#f97316;color:#fff;';
  reload.addEventListener('click', () => window.location.reload());

  const home = document.createElement('button');
  home.textContent = 'Go to Home';
  home.style.cssText =
    buttonStyle +
    'background:transparent;color:#fff;border:1px solid rgba(255,255,255,0.4);';
  home.addEventListener('click', () =>
    window.location.assign(import.meta.env.BASE_URL || '/'),
  );

  overlay.append(title, body, reload, home);
  document.body.appendChild(overlay);
}

/**
 * React 19 root error hooks. `onUncaughtError` fires only for errors NOT caught by an
 * error boundary — i.e. a genuinely fatal render crash that blanked the tree — so that is
 * the precise signal to show the recovery overlay. Caught/recoverable errors are recorded
 * for diagnostics only.
 */
export const rootErrorHandlers = {
  // NOTE: providing these handlers REPLACES React's default console reporting, and the
  // existing remote telemetry (OpenReplay) captures console output — so each handler
  // must log as well as record, or render errors silently vanish from remote capture.
  onUncaughtError: (error: unknown, errorInfo: { componentStack?: string }) => {
    console.error(
      'Uncaught render error:',
      error,
      errorInfo?.componentStack ?? '',
    );
    crashDiagnostics.record('react.uncaught', error, errorInfo?.componentStack);
    showRecoveryOverlay();
  },
  onCaughtError: (error: unknown, errorInfo: { componentStack?: string }) => {
    console.error(
      'Render error caught by boundary:',
      error,
      errorInfo?.componentStack ?? '',
    );
    crashDiagnostics.record('react.caught', error, errorInfo?.componentStack);
  },
  onRecoverableError: (error: unknown) => {
    console.error('Recoverable render error:', error);
    crashDiagnostics.record('react.recoverable', error);
  },
};

/**
 * Global observability handlers: record errors and unhandled promise rejections. These
 * only RECORD (no overlay) — many non-fatal errors fire here and must not blank the app;
 * the fatal-render signal is `onUncaughtError` above.
 */
export function installGlobalErrorHandlers(): void {
  if (typeof window === 'undefined') return;
  window.addEventListener('error', (event) => {
    crashDiagnostics.record('window.error', event.error ?? event.message);
  });
  window.addEventListener('unhandledrejection', (event) => {
    crashDiagnostics.record('unhandledrejection', event.reason);
  });
}
